import type { Release, ReleaseAdapterOwnedResource, ReleaseAdapterOwnedResources } from "@packages/commons";

import { adapterDefinitions } from "@/features/adapters/model/definitions";
import { getAdapterPersistenceId } from "@/features/adapters/model/helpers";
import { getResourceStepId } from "@/features/adapters/model/helpers";
import type { AdapterDefinition, AdapterResourceKind } from "@/features/adapters/model/types";

export type ManagedResourceKind = AdapterResourceKind;

export interface ManagedResourceOwner {
  definition: AdapterDefinition;
  kind: ManagedResourceKind;
  /** The workflow step that owns the resource. */
  stepId: string;
  definitionKey: string;
}

export const getOwnedResources = (
  ownedResources: ReleaseAdapterOwnedResources | undefined,
  stepId?: string,
): ReleaseAdapterOwnedResource[] =>
  (ownedResources?.resources ?? []).filter((resource) => !stepId || resource.stepId === stepId);

export const getOwnedResource = (
  ownedResources: ReleaseAdapterOwnedResources | undefined,
  stepId: string | undefined,
  definitionKey?: string,
): ReleaseAdapterOwnedResource | undefined =>
  stepId
    ? getOwnedResources(ownedResources, stepId).find(
        (resource) => definitionKey === undefined || resource.definitionKey === definitionKey,
      )
    : undefined;

export const setOwnedResource = <T extends ReleaseAdapterOwnedResources>(
  ownedResources: T,
  resource: ReleaseAdapterOwnedResource,
): T & { resources: ReleaseAdapterOwnedResource[] } => ({
  ...ownedResources,
  resources: [
    ...getOwnedResources(ownedResources).filter(
      (current) => current.stepId !== resource.stepId || current.definitionKey !== resource.definitionKey,
    ),
    resource,
  ],
});

export const removeOwnedResource = <T extends ReleaseAdapterOwnedResources>(
  ownedResources: T,
  stepId: string,
  definitionKey: string,
): T & { resources: ReleaseAdapterOwnedResource[] } => ({
  ...ownedResources,
  resources: getOwnedResources(ownedResources).filter(
    (resource) => resource.stepId !== stepId || resource.definitionKey !== definitionKey,
  ),
});

export const createResourceIdResolver = (
  definition: AdapterDefinition,
  ownedResources: ReleaseAdapterOwnedResources | undefined,
) => (kinds: readonly AdapterResourceKind[], definitionKey: string): string | undefined =>
  getOwnedResource(
    ownedResources,
    getResourceStepId(definition, kinds, definitionKey),
    definitionKey,
  )?.resourceId;

export const getManagedResourceOwner = (
  release: Pick<Release, "adapters">,
  kind: ManagedResourceKind,
  resourceId: string | undefined,
): ManagedResourceOwner | undefined => {
  if (!resourceId) return undefined;

  for (const definition of adapterDefinitions) {
    const adapter = release.adapters?.find((candidate) => candidate.format === 2 && candidate.id === getAdapterPersistenceId(definition.id) && candidate.enabled);
    const owned = getOwnedResources(adapter?.ownedResources).find(
      (candidate) => candidate.kind === kind && candidate.resourceId === resourceId,
    );
    if (owned) return { definition, kind, stepId: owned.stepId, definitionKey: owned.definitionKey };
  }

  return undefined;
};
